import { patientApi } from '../api';
import { patientAuthFlow } from '../auth/patientAuthFlow';
import {
  isCanonicalNH12,
  formatNeoHealthCardId,
} from "@shared/identity/neohealthcard-id-adapter";

export interface PatientIdentity {
  patientName: string;
  nhcIdRaw: string;
  nhcIdDisplay: string;
  nhcIdShort: string;
  neoVerifyId: string;
  abhaId: string;
  bloodGroup: string;
  dob: string;
  gender: string;
  insuranceStatus: string;
  phone: string;
  email: string;
  address: string;
  verifiedStatus: boolean;
  qrEnabled: boolean;
  nfcReady: boolean;
}

/**
 * What onboarding recorded on THIS device.
 *
 * Used only to fill gaps while the profile has not loaded — never to override
 * it. These values are a snapshot of a form the patient filled once, kept in
 * this browser's storage: change a name on the phone and this device would go
 * on showing the old one, because the snapshot won every field it had.
 *
 * `verifiedStatus` was the worst of it — a locally held flag could assert that
 * an account was verified. That is the server's answer and only the server's.
 */
function getOnboardingOverrides(): Partial<PatientIdentity> | null {
  const s = patientAuthFlow.getState();
  if (!s.nhcIdGenerated || !s.nhcId) return null;
  if (s.approvalStatus !== 'approved') return null;
  const canonical = isCanonicalNH12(s.nhcId);
  const shortId = canonical
    ? s.nhcId.replace(/\D/g, '').slice(-6)
    : s.nhcId.replace('NHC-', '');
  return {
    patientName: s.fullName || undefined,
    nhcIdRaw: s.nhcId,
    nhcIdDisplay: canonical ? formatNeoHealthCardId(s.nhcId) : s.nhcId,
    nhcIdShort: shortId,
    neoVerifyId: s.neoVerifyId || undefined,
    bloodGroup: s.bloodGroup || undefined,
    dob: s.dob || undefined,
    gender: s.gender || undefined,
    phone: s.phone || undefined,
    email: s.email || undefined,
    address: [s.address, s.city, s.state, s.pin].filter(Boolean).join(', ') || undefined,
    verifiedStatus: true,
  };
}

export function buildIdentity(profile: any): PatientIdentity {
  const onboarding = getOnboardingOverrides();
  const nhcIdRaw = profile?.nhcId || onboarding?.nhcIdRaw || '';
  // A missing/sentinel NHC ID means the real one couldn't be loaded — show
  // that honestly rather than generating a fabricated ID client-side.
  const isSentinel = nhcIdRaw === 'NHC-000000' || nhcIdRaw === '';
  const canonical = !isSentinel && isCanonicalNH12(nhcIdRaw);
  const shortId = isSentinel
    ? ''
    : canonical
      ? nhcIdRaw.replace(/\D/g, '').slice(-6)
      : nhcIdRaw.replace('NHC-', '');
  // Verification is a backend decision: the auth flow records approvalStatus
  // from real register/login/OTP responses. Never infer "verified" from the
  // mere presence of an NHC ID, and never synthesize a NeoVerify ID here —
  // it is server-issued or absent.
  const backendApproved = patientAuthFlow.getState().approvalStatus === 'approved';

  // The server first, this device's onboarding snapshot only where the server
  // said nothing — the reverse of what this used to do.
  return {
    patientName: profile?.fullName || onboarding?.patientName || 'Patient',
    nhcIdRaw: isSentinel ? '' : nhcIdRaw,
    nhcIdDisplay: isSentinel ? 'Not available' : canonical ? formatNeoHealthCardId(nhcIdRaw) : nhcIdRaw,
    nhcIdShort: shortId,
    neoVerifyId: profile?.neoVerifyId || onboarding?.neoVerifyId || '',
    abhaId: profile?.abhaId || '',
    bloodGroup: profile?.bloodGroup || onboarding?.bloodGroup || 'N/A',
    dob: profile?.dateOfBirth || onboarding?.dob || 'Not set',
    gender: profile?.gender || onboarding?.gender || 'Not set',
    insuranceStatus: profile?.insuranceProvider ? 'Linked' : 'Not linked',
    phone: profile?.phone || onboarding?.phone || 'Not set',
    email: profile?.email || onboarding?.email || 'Not set',
    address: profile?.address || onboarding?.address || 'Not set',
    /**
     * The server's answer alone. A device-held flag must never be able to
     * assert that an account is verified, and `identityVerified` is the field
     * the server actually decides it with.
     */
    verifiedStatus: profile?.identityVerified === true || backendApproved,
    qrEnabled: true,
    nfcReady: true,
  };
}

export function titaniumCardDetails(id: PatientIdentity) {
  return [
    { label: 'Blood', value: id.bloodGroup },
    { label: 'DOB', value: id.dob },
    { label: 'Gender', value: id.gender },
    { label: 'Insurance', value: id.insuranceStatus === 'Linked' ? 'Linked' : 'Not linked' },
  ];
}

export async function loadPatientIdentity(): Promise<{ identity: PatientIdentity; profile: any }> {
  try {
    // The full profile — the dashboard summary's profile stub only carries
    // name + NHC ID, which left blood group / phone / email / ABHA blank
    // everywhere identity is shown (profile card, verification strip).
    const profile = await patientApi.getProfile();
    return { identity: buildIdentity(profile), profile };
  } catch {
    return { identity: buildIdentity(null), profile: null };
  }
}
